/**
 * Contextual page notes — types, API wrappers, and the PAGE_KEYS registry.
 *
 * Issue #486; spec at docs/plan-contextual-page-notes.md.
 *
 * Every signed-in user may read an active page note. `useCanEditPageNotes()`
 * (from contexts/PageNotesContext) gates authoring and history; the backend
 * enforces the same division for every endpoint.
 *
 * The PAGE_KEYS const is the developer-facing registry. The backend accepts
 * anything matching `^[a-z][a-z0-9_]*$`; TypeScript catches typos here.
 */
import { api } from './api';

// ── PAGE_KEYS registry ────────────────────────────────────────────────
// Add a new entry whenever a screen wants an (i) marker. Mount the marker
// with `<PageNoteMarker noteKey={PAGE_KEYS.FOO} />`.

export const PAGE_KEYS = {
  DISCOVERY_REQUESTS: 'discovery_requests',
  RECEIVED_PRODUCTIONS: 'received_productions',
  DISCOVERY_RESPONSES: 'discovery_responses',
  ASK_AI: 'ask_ai',
  CASE_ISSUES: 'case_issues',
  PLEADINGS: 'pleadings',
  CHRONOLOGY: 'chronology',
  CLAIMS_MATRIX: 'claims_matrix',
  WORKBENCH: 'workbench',
} as const;

export type PageKey = (typeof PAGE_KEYS)[keyof typeof PAGE_KEYS];

// ── Wire types ────────────────────────────────────────────────────────

export interface PageNoteVersion {
  version_id: string;
  version_no: number;
  body_markdown: string;
  change_note: string | null;
  author_email: string;
  created_at: string;
}

export interface PageNoteCurrent {
  page_key: string;
  current: PageNoteVersion | null;
  archived: boolean;
}

export interface CreateVersionPayload {
  body_markdown: string;
  change_note?: string;
}

// ── API wrappers ──────────────────────────────────────────────────────

export const pageNotesApi = {
  /**
   * Boolean staff gate for the (i) marker chrome. Open to any authenticated
   * user — non-staff get `{can_edit: false}`, NOT a 403. They may still read
   * a published page note, but cannot create, edit, archive, or inspect its
   * editorial history.
   */
  canEdit: () => api.get<{ can_edit: boolean }>('/me/can-edit-page-notes'),

  /** Fetch the current version + archived flag for a page_key. */
  get: (pageKey: PageKey | string) =>
    api.get<PageNoteCurrent>(`/page-notes/${encodeURIComponent(pageKey)}`),

  /** History list — newest first. */
  listVersions: (pageKey: PageKey | string) =>
    api.get<PageNoteVersion[]>(
      `/page-notes/${encodeURIComponent(pageKey)}/versions`,
    ),

  /** Append a new version. Lazy-creates the page_notes row on first save. */
  createVersion: (pageKey: PageKey | string, payload: CreateVersionPayload) =>
    api.post<PageNoteVersion>(
      `/page-notes/${encodeURIComponent(pageKey)}/versions`,
      payload,
    ),

  /** Archive / unarchive. */
  archive: (pageKey: PageKey | string) =>
    api.post<{ archived: boolean }>(
      `/page-notes/${encodeURIComponent(pageKey)}/archive`,
    ),

  unarchive: (pageKey: PageKey | string) =>
    api.post<{ archived: boolean }>(
      `/page-notes/${encodeURIComponent(pageKey)}/unarchive`,
    ),
};

// ── Display helpers ───────────────────────────────────────────────────

/**
 * "5 hr ago", "3 days ago", "just now" — matches the relative-time shape
 * the magic-patterns mockup uses in the panel footer + history list.
 */
export function relativeTime(iso: string): string {
  const now = Date.now();
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const diff = Math.max(0, now - then);
  const sec = Math.round(diff / 1000);
  if (sec < 60) return 'just now';
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day} day${day === 1 ? '' : 's'} ago`;
  const mon = Math.round(day / 30);
  return `${mon} mo ago`;
}
